import { DecisionGraph, type LayoutNode, type LayoutEdge } from "@imminently/decisively-core";
import { AutoFitGraph, scrollableMixin } from "@/common";
import * as FilterDatesControlNS from "@/components/RuleGraph_FullRelease/DatesFilter";
import { nodeTypes } from "@/components/RuleGraph_FullRelease/__nodeTypes";
import { isTemporal } from "@/pages/decisions/DecisionDashboard/Reports/Details/UnifiedTimeline/types";
import type { GraphNode, RawRuleGraph } from "@imminently/decisively-core";
import TemporalValue from "@imminently/decisively-ui/TemporalValue";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import get from "lodash/get";

import styled from "@emotion/styled";
import {
  Controls,
  type Edge as FlowEdgeFromLib,
  type Node as FlowNodeFromLib,
  ReactFlow,
  type ReactFlowProps,
  ReactFlowProvider,
  isNode,
} from "@xyflow/react";
import React from "react";
import GraphNodeFocusMode from "../RuleGraph_FullRelease/components/GraphNodeFocusMode";
import GraphPallet from "../RuleGraph_FullRelease/components/GraphPallet";
import { layout } from "@/lib/graphLayout";

const NODE_WIDTH = 200;
const NODE_HEIGHT = 300;

export type GraphNodeForLayout = {
  label: string;
  node: LayoutNode<GraphNode>;
  value: GraphNode["input"] | GraphNode["derived"];
  type: "byValue";
  width: number;
  height: number;
};

export type FlowNode = FlowNodeFromLib<{
  label: React.ReactNode;
  node: LayoutNode<GraphNode>;
  value: LayoutNode<GraphNodeForLayout>["value"];
}> & {
  width: number;
  height: number;
};
export type FlowEdge = FlowEdgeFromLib & {
  points: LayoutEdge["points"];
};

const StyledControls = styled(Controls)`
  bottom: 10px;
  top: inherit;
`;

const StyledFlow = styled(ReactFlow as any)`
  .xyflow__node {
    width: 230px;
  }
`;

// ===================================================================================

const StyledBreadcrumbs = styled(Breadcrumbs)`
  background-color: white;
  white-space: nowrap;
  padding-bottom: 0.75rem;
  margin-bottom: -0.5rem;
  overflow: auto;
  ${scrollableMixin};

  .MuiBreadcrumbs-ol { flex-wrap: nowrap; }
`;

const Breadcrumb = styled(Typography)`
  cursor: pointer;
  text-decoration: underline;
`;

export type BreadcrumbsCompProps = {
  clickHistory: Array<{
    id: string;
    description?: string | undefined;
  }>;
  navigate: (index: number) => unknown;
  clearFocusNodeClickHistory: () => unknown;
};

export const BreadcrumbsComp: React.FC<BreadcrumbsCompProps> = React.memo((p) => {
  const { clickHistory, navigate, clearFocusNodeClickHistory } = p;

  const onClick = React.useCallback<React.MouseEventHandler<HTMLParagraphElement>>(
    (e) => {
      const { index } = e.currentTarget.dataset;
      if (index === undefined) return;

      navigate(Number(index));
    },
    [navigate],
  );

  React.useEffect(() => () => void clearFocusNodeClickHistory(), [clearFocusNodeClickHistory]);

  return clickHistory.length === 0 ? (
    <div />
  ) : (
    <StyledBreadcrumbs>
      {clickHistory.map(({ id, description }, i) => {
        const title = description || id;
        const shortTitle = title.length > 30 ? `${title.slice(0, 30)}…` : title;

        return (
          <Breadcrumb
            title={title}
            onClick={onClick}
            data-index={i}
            key={id}
          >
            {shortTitle}
          </Breadcrumb>
        );
      })}
    </StyledBreadcrumbs>
  );
});
BreadcrumbsComp.displayName = "components/FocusGraph/BreadcrumbsComp";

// ===================================================================================

const buildNode = (data: LayoutNode<GraphNode>): GraphNodeForLayout => {
  return {
    label: data.description ?? "",
    node: data,
    value: get(data, "input", get(data, "derived")),
    type: "byValue",
    width: NODE_WIDTH,
    // Approx 12 chars per line, each line needs about 50 pixels
    height: Math.max(NODE_HEIGHT, (get(data, "description", "1").length / 12) * 25),
  };
};
const buildNodeInner = buildNode;

const StyledFilterDatesControl = styled(FilterDatesControlNS._)`
  flex-shrink: 0;
`;

const TypedFlowProvider = ReactFlowProvider as React.FC<{ children: React.ReactNode }>;

export type FocusGraphProps = {
  /** The graph the focus node is in (a DecisionGraph; any other form is read as one). */
  graph: DecisionGraph | RawRuleGraph;
  focusNode: GraphNode;
  showHidden?: boolean;
  showValues?: boolean;
  datesFilter?: FilterDatesControlNS.Props["value"];
  setDatesFilter?: FilterDatesControlNS.Props["set"];
  BreadcrumbsJSX?: React.ReactElement;
  onRawNodeClick: (n: LayoutNode<GraphNode>) => unknown;
};

const GRAPH_STYLES = { width: "100%" /* , minHeight: '600px' */ };

export const FocusGraph: React.FC<FocusGraphProps> = React.memo((p) => {
  const {
    graph,
    focusNode,
    showHidden = false,
    showValues = true,
    datesFilter,
    setDatesFilter,
    BreadcrumbsJSX,
    onRawNodeClick,
  } = p;

  const p_graph = React.useMemo(() => DecisionGraph.from(graph as any), [graph]);

  // // useEffect(() => {
  // //   setFocusNode(findNode(graph.nodes, goal));
  // //   // Build the graph from this goal
  // // }, [goal, graph, setFocusNode]);

  const { nodes, edges } = React.useMemo(() => {
    if (!focusNode)
      return {
        nodes: [],
        edges: [],
      };

    const buildNode = (data) => {
      // return buildNodeInner(data);
      if (!datesFilter || datesFilter[0] === null || datesFilter[1] === null) return buildNodeInner(data);

      return buildNodeInner({
        ...data,
        derived: FilterDatesControlNS.mergeValueAndDatesFilter(data.derived, datesFilter),
      });
    };

    // Now build the graph from this focus node: the node and its (shown) inputs and outputs

    // Add the root node - everything will point to this
    const path = (() => {
      if (focusNode.entity === "global") return focusNode.id;
      if (focusNode.hidden) return focusNode.id;
      const path = `${focusNode.entity}/${focusNode.index}/${focusNode.id}`;

      return focusNode.parent ? `${focusNode.parent}/${path}` : path;
    })();
    const root = p_graph.ref(path);

    const drawn = new Map<number, GraphNodeForLayout>();
    if (root !== undefined) {
      drawn.set(root, buildNode(focusNode));
      for (const id of [...p_graph.predecessors(root), ...p_graph.successors(root)]) {
        if (drawn.has(id) || !p_graph.isListed(id)) continue;
        if (!showHidden && p_graph.field(id, "hidden")) continue;
        drawn.set(id, buildNode(p_graph.node(id)));
      }
    }

    const placed = layout(p_graph, { nodes: drawn.keys(), size: (id) => drawn.get(id)!, nodesep: 100 });

    const nodes: FlowNode[] = [];
    const edges: FlowEdge[] = [];
    let type, style;
    for (const [id, node] of drawn) {
      if (!showHidden && node.node.hidden) continue;
      // goals and sources as the rule graph has them (not just within what's drawn)
      if (showValues) {
        type = "byValue";
      } else if (p_graph.predecessors(id).length === 0) type = "goal";
      else if (p_graph.successors(id).length === 0) type = "source";
      else type = "intermediate";

      let valueDesc: React.ReactNode = "";
      let label: React.ReactNode = node.label;
      if (node.node.index) label = `${label} (${node.node.entity}:${node.node.index})`;
      if (node.value === false) {
        valueDesc = "false";
      } else if (node.value === null) {
        valueDesc = "Uncertain";
      } else if (typeof node.value === "undefined") {
        valueDesc = "Unknown";
      } else if (get(node, "value.temporal")) {
        valueDesc = (
          <TemporalValue
            inline
            value={get(node, "value") || []}
          />
        );
      } else valueDesc = node.value.toString();

      if (showValues) {
        label = (
          <div>
            <p>{label}</p>
            <em>{valueDesc}</em>
          </div>
        );
      }

      const centre = placed.nodes.get(id)!;
      nodes.push({
        id: id === root ? path : p_graph.deref(id),
        type,
        style,
        data: {
          label,
          node: node.node,
          value: node.value,
        },
        width: node.width,
        height: node.height,
        position: {
          x: centre.x - node.width / 2,
          y: centre.y - node.height / 2,
        },
      });
    }
    for (const { from, to, points } of placed.edges) {
      const v = from === root ? path : p_graph.deref(from);
      const w = to === root ? path : p_graph.deref(to);
      edges.push({
        id: `__${v}__${w}`,
        points,
        source: v,
        target: w,
        animated: true,
        label: "",
      });
    }

    return { nodes, edges };
  }, [focusNode, p_graph, showHidden, showValues, datesFilter]);

  const onElementClick = React.useCallback<NonNullable<ReactFlowProps["onNodeClick"]>>(
    (_, element) => {
      if (!isNode(element)) return;

      const { data } = element as FlowNode;
      if (data === undefined) return;

      onRawNodeClick(data.node);
    },
    [onRawNodeClick],
  );

  const derivedValues = React.useMemo(() => p_graph.ids().map((id) => p_graph.field(id, "derived")), [p_graph]);

  const hideFilterDatesControl = React.useMemo(() => {
    const someValueIsTemporal = derivedValues.some((derived) => isTemporal(derived as any));

    return someValueIsTemporal === false || undefined;
  }, [derivedValues]);

  const minMax = React.useMemo(
    () => FilterDatesControlNS.deriveMinMaxFromNodeValues(derivedValues as any),
    [derivedValues],
  );

  return (
    <TypedFlowProvider>
      <AutoFitGraph />
      <StyledFlow
        nodesDraggable
        nodeTypes={{
          ...(nodeTypes as any),
          // intermediate: GraphNodeFocusMode,
          // goal: GraphNodeFocusMode,
          // source: GraphNodeFocusMode,
          byValue: GraphNodeFocusMode,
        }}
        nodes={nodes}
        edges={edges}
        minZoom={0.01}
        style={GRAPH_STYLES}
        nodesConnectable={false}
        onNodeClick={onElementClick}
      >
        <StyledControls />
        <div className="relative z-[4] mx-8 flex items-center justify-between gap-4 bg-white pb-1">
          {BreadcrumbsJSX}
          {setDatesFilter === undefined ? null : (
            <StyledFilterDatesControl
              hide={hideFilterDatesControl}
              minMax={minMax}
              set={setDatesFilter}
              value={datesFilter}
            />
          )}
        </div>
        <GraphPallet />
      </StyledFlow>
    </TypedFlowProvider>
  );
});
FocusGraph.displayName = "components/FocusGraph";
